import {
  BillingCycle,
  BillingCycleDependent,
  ComingSoon,
  FullySupported,
  LimitedByQuantity,
  PlanFeatureCategory,
  PlanFeatureLevelSupport,
  PlanFeaturesMetadata,
  PlanType,
  Unlimited,
  Unsupported,
} from '@/features/common/pricing';
import { IProductMetadata } from '@/features/pricingV2/domain';

type FeatureSupportByPlan =
  PlanFeaturesMetadata[PlanFeatureCategory]['features'][number]['supportByPlan'];

export type FeatureMapper = (metadata: IProductMetadata) => PlanFeatureLevelSupport;
export type Mapper = (metadata: IProductMetadata[]) => FeatureSupportByPlan;

export function createMapper(
  productsMetadata: IProductMetadata[],
  featureMapper: FeatureMapper,
  overrids: FeatureSupportByPlan = {},
): FeatureSupportByPlan {
  return Object.assign(
    {},
    productsMetadata.reduce((acc, metadata) => {
      acc[metadata.family] = featureMapper(metadata);
      return acc;
    }, {} as FeatureSupportByPlan),
    overrids,
  );
}

export const mapAmountOfCredits: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, (metadata) => {
    function mapCredit(limit: number | 'unlimited'): LimitedByQuantity | Unlimited {
      return limit === 'unlimited' ? new Unlimited() : new LimitedByQuantity({ limit });
    }

    return new BillingCycleDependent({
      [BillingCycle.Monthly]: mapCredit(metadata.variants.Monthly.fullCredits),
      [BillingCycle.Yearly]: mapCredit(metadata.variants.Yearly.fullCredits),
    });
  });
};

export const mapExportCredits: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new Unlimited());
};

export const mapLinkedinExt: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapUniversalExt: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapDashboard: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapTopUpCredits: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, (metadata) => {
    switch (metadata.family) {
      case PlanType.Pro:
      case PlanType.ProPlus:
        return new FullySupported();
      case PlanType.Unlimited:
      case PlanType.Custom:
        return new Unlimited();
      default:
        return new Unsupported();
    }
  });
};

export const mapBulkSave: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, (metadata) => {
    return new BillingCycleDependent({
      [BillingCycle.Monthly]: metadata.variants.Monthly.features.includes('bulk_save')
        ? new FullySupported()
        : new Unsupported(),
      [BillingCycle.Yearly]: metadata.variants.Yearly.features.includes('bulk_save')
        ? new FullySupported()
        : new Unsupported(),
    });
  });
};

export const mapEnrichCsv: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, (metadata) => {
    return new BillingCycleDependent({
      [BillingCycle.Monthly]: metadata.variants.Monthly.features.includes(
        'csv_enrichment',
      )
        ? new FullySupported()
        : new Unsupported(),
      [BillingCycle.Yearly]: metadata.variants.Yearly.features.includes('csv_enrichment')
        ? new FullySupported()
        : new Unsupported(),
    });
  });
};

export const mapListManagement: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapTagManagement: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapExportToCsv: Mapper = (productsMetadata) => {
  return createMapper(
    productsMetadata,
    (metadata) => {
      return new BillingCycleDependent({
        [BillingCycle.Monthly]: metadata.variants.Monthly.product?.features.includes(
          'data_export',
        )
          ? new FullySupported()
          : new Unsupported(),
        [BillingCycle.Yearly]: metadata.variants.Yearly.product?.features.includes(
          'data_export',
        )
          ? new FullySupported()
          : new Unsupported(),
      });
    },
    {
      [PlanType.Custom]: new FullySupported(),
    },
  );
};

export const mapSaveToCrm: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, (metadata) => {
    return new BillingCycleDependent({
      [BillingCycle.Monthly]: metadata.variants.Monthly.features.includes(
        'crm_integration',
      )
        ? new FullySupported()
        : new Unsupported(),
      [BillingCycle.Yearly]: metadata.variants.Yearly.features.includes('crm_integration')
        ? new FullySupported()
        : new Unsupported(),
    });
  });
};

export const mapSaveToCrmComingSoon: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, (metadata) => {
    return new BillingCycleDependent({
      [BillingCycle.Monthly]: metadata.variants.Monthly.features.includes(
        'crm_integration',
      )
        ? new ComingSoon()
        : new Unsupported(),
      [BillingCycle.Yearly]: metadata.variants.Yearly.features.includes('crm_integration')
        ? new ComingSoon()
        : new Unsupported(),
    });
  });
};

export const mapUsageAnalytics: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapTeamUsageAnalytics: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported(), {
    [PlanType.Free]: new Unsupported(),
  });
};

export const mapGdprCompliant: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapCcpaCompliant: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapGoogle: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapMicrosoft: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapOkta: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new Unsupported(), {
    [PlanType.Custom]: new FullySupported(),
  });
};

export const mapSaml: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new Unsupported(), {
    [PlanType.Custom]: new FullySupported(),
  });
};

export const mapHelpCenter: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapEmailSupport: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported());
};

export const mapLiveChat: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new FullySupported(), {
    [PlanType.Free]: new Unsupported(),
  });
};

export const mapOnboarding: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new Unsupported(), {
    [PlanType.Custom]: new FullySupported(),
  });
};

export const mapDedicatedSupport: Mapper = (productsMetadata) => {
  return createMapper(productsMetadata, () => new Unsupported(), {
    [PlanType.Custom]: new FullySupported(),
  });
};
